/**
 * <DraftRefinementCard /> — renders a `render: "draft"` tool result
 * (M23).
 *
 * The M23 generate tools (draft_policy, draft_job_description,
 * draft_termination_reason, translate_field, summarize_document)
 * return a DRAFT. This card shows it with three affordances:
 *   - Edit      — inline textarea editing of the draft text.
 *   - Regenerate— asks the assistant to produce a fresh draft.
 *   - Save as draft — persists it for HR review (PR2 wires the
 *     endpoint; until then the button is omitted).
 *
 * Nothing here mutates live config — the draft is a proposal.
 */

import { Check, Pencil, RotateCcw } from "lucide-react";
import { useState } from "react";

import { AIBadge } from "../ui/AIBadge";
import { ChatMarkdown } from "./ChatMarkdown";

export interface DraftRefinementCardProps {
  summary: string;
  data: Record<string, unknown>;
  /** Send a follow-up message to the assistant (e.g. "regenerate"). */
  onSendMessage?: (text: string) => void;
  /** Persist the (possibly edited) draft. Omitted until PR2. */
  onSaveDraft?: (draft: {
    kind: string;
    title: string;
    content: string;
  }) => void;
}

export function DraftRefinementCard({
  summary,
  data,
  onSendMessage,
  onSaveDraft,
}: DraftRefinementCardProps) {
  const kind = typeof data.kind === "string" ? data.kind : "draft";
  const title =
    typeof data.title === "string" ? data.title : "Draft";
  const original =
    typeof data.content === "string" ? data.content : summary;

  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(original);
  const [saved, setSaved] = useState(false);

  return (
    <div className="rounded-lg border border-ai-subtle bg-ai-soft">
      <header className="flex items-center gap-1.5 border-b border-ai-subtle px-2.5 py-1.5">
        <AIBadge />
        <span className="text-xs font-semibold text-text-primary">
          {title}
        </span>
        <span className="ms-auto rounded bg-surface-muted px-1.5 py-0.5 text-xs uppercase text-text-tertiary">
          draft
        </span>
      </header>

      {editing ? (
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={8}
          className="w-full resize-y bg-transparent px-2.5 py-2 text-xs text-text-primary outline-none"
        />
      ) : (
        // Draft text is LLM-generated — render its markdown.
        <div className="px-2.5 py-2">
          <ChatMarkdown>{content}</ChatMarkdown>
        </div>
      )}

      <footer className="flex flex-wrap items-center gap-1.5 border-t border-ai-subtle px-2.5 py-1.5">
        {editing ? (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="inline-flex items-center gap-1 rounded border border-border-subtle px-1.5 py-0.5 text-xs text-text-secondary hover:bg-surface-muted"
          >
            <Check size={10} /> Done editing
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 rounded border border-border-subtle px-1.5 py-0.5 text-xs text-text-secondary hover:bg-surface-muted"
          >
            <Pencil size={10} /> Edit
          </button>
        )}
        {onSendMessage && (
          <button
            type="button"
            onClick={() =>
              onSendMessage(`Regenerate the ${kind} draft.`)
            }
            className="inline-flex items-center gap-1 rounded border border-border-subtle px-1.5 py-0.5 text-xs text-text-secondary hover:bg-surface-muted"
          >
            <RotateCcw size={10} /> Regenerate
          </button>
        )}
        {onSaveDraft &&
          (saved ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-emerald-700">
              <Check size={10} /> Saved for review
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                onSaveDraft({ kind, title, content });
                setSaved(true);
              }}
              className="inline-flex items-center gap-1 rounded bg-brand px-1.5 py-0.5 text-xs font-medium text-brand-fg hover:opacity-90"
            >
              <Check size={10} /> Save as draft
            </button>
          ))}
      </footer>
    </div>
  );
}
